import { Controller } from '@hotwired/stimulus';
import { Lithuanian } from 'flatpickr/dist/l10n/lt';
import { english } from 'flatpickr/dist/l10n/default';

export default class extends Controller {
    static values = {
        events: Object,
        timezone: String
    }

    static targets = ['calendar', 'eventDetail', 'moreInfoBtn']

    connect() {
        super.connect();
        this.contextMenu = null;
        this.currentEvents = null;
        this.load();

        document.addEventListener('click', this.closeContextMenu.bind(this));
    }

    disconnect() {
        document.removeEventListener('click', this.closeContextMenu.bind(this));
        if (this.contextMenu) {
            this.contextMenu.remove();
        }
    }

    async load() {
        const flatpickr = (await import('flatpickr')).default;

        this.flatpickrInstance = flatpickr(this.calendarTarget, {
            inline: true,
            appendTo: this.calendarTarget,
            monthSelectorType: 'static',
            locale: await this.getLocale(),
            onDayCreate: this.handleDayCreate.bind(this),
            onChange: this.handleDateChange.bind(this),
            onReady: this.customizeArrows.bind(this),
        });
    }

    async getLocale() {
        switch (document.documentElement.lang.split('-')[0]) {
            case 'lt':
                return Lithuanian;
            default:
                return english;
        }
    }

    handleDayCreate(dObj, dStr, fp, dayElem) {
        const textContent = dayElem.textContent;
        dayElem.innerHTML = `<span>${textContent}</span>`;

        if (!this.hasEventsValue) return;

        const date = dayElem.dateObj;
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        if (this.eventsValue[formattedDate]) {
            const events = this.eventsValue[formattedDate];
            dayElem.classList.add('has-event');
            dayElem.dataset.events = JSON.stringify(events);
            dayElem.dataset.date = formattedDate;

            dayElem.addEventListener('click', this.handleDayClick.bind(this));
        }
    }

    handleDayClick(event) {
        event.stopPropagation();
        const dayElem = event.currentTarget;
        const events = JSON.parse(dayElem.dataset.events);

        if (events.length > 1) {
            this.showContextMenu(dayElem, events);
        } else {
            this.closeContextMenu();
            this.selectEvent(events[0]);
        }
    }

    showContextMenu(dayElem, events) {
        this.closeContextMenu();

        this.currentEvents = events;
        this.contextMenu = document.createElement('div');
        this.contextMenu.className = 'calendar-context-menu';

        const menuList = document.createElement('ul');
        menuList.className = 'calendar-context-menu__list';

        events.forEach((event, index) => {
            const item = document.createElement('li');
            item.className = 'calendar-context-menu__item';
            item.innerHTML = `
                <span class="calendar-context-menu__title">${this.escapeHtml(event.title)}</span>
                ${event.time ? `<span class="calendar-context-menu__time">${this.escapeHtml(event.time)}</span>` : ''}
            `;
            item.addEventListener('click', (e) => {
                e.stopPropagation();
                this.selectEvent(event);
                this.closeContextMenu();
            });
            menuList.appendChild(item);
        });

        this.contextMenu.appendChild(menuList);

        const rect = dayElem.getBoundingClientRect();
        const calendarRect = this.calendarTarget.getBoundingClientRect();

        this.contextMenu.style.position = 'absolute';
        this.contextMenu.style.top = `${rect.bottom - calendarRect.top + 5}px`;
        this.contextMenu.style.left = `${rect.left - calendarRect.left}px`;

        this.calendarTarget.appendChild(this.contextMenu);
        dayElem.classList.add('tooltip-active');

        requestAnimationFrame(() => {
            const menuRect = this.contextMenu.getBoundingClientRect();
            if (menuRect.right > window.innerWidth) {
                this.contextMenu.style.left = `${rect.right - calendarRect.left - menuRect.width}px`;
            }
        });
    }

    closeContextMenu(event) {
        if (this.contextMenu) {
            this.contextMenu.remove();
            this.contextMenu = null;

            this.calendarTarget.querySelectorAll('.tooltip-active').forEach(day => {
                day.classList.remove('tooltip-active');
            });
        }
    }

    selectEvent(event) {
        this.showEventDetails([event]);
    }

    handleDateChange(selectedDates) {
        if (selectedDates.length === 0) return;

        const date = selectedDates[0];
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        if (this.eventsValue[formattedDate]) {
            this.showEventDetails(this.eventsValue[formattedDate]);
        }
    }

    showEventDetails(events) {
        if (!this.hasEventDetailTarget) return;

        const event = events[0];

        console.log(this.hasMoreInfoBtn)
        if (this.hasMoreInfoBtnTarget) {
            this.moreInfoBtnTarget.href = event.url;
            console.log(this.hasMoreInfoBtn)
        }

        this.eventDetailTarget.innerHTML = this.renderEventCard(event);
    }

    renderEventCard(event) {
        const tag = event.url ? 'a' : 'div';
        const hrefAttr = event.url ? `href="${this.escapeHtml(event.url)}"` : '';

        return `
        <${tag} ${hrefAttr} class="event-card">
            ${event.image ? `
                <div class="event-card__image">
                    <img src="${this.escapeHtml(event.image)}" alt="${this.escapeHtml(event.title)}">
                </div>
            ` : ''}
            
            <div class="event-card__content">
                ${event.title ? `
                    <h3 class="event-card__title">${this.escapeHtml(event.title)}</h3>
                ` : ''}
                
                ${event.event_time ? `
                    <p class="event-card__time">${this.escapeHtml(event.event_time)}</p>
                ` : ''}
                
                ${event.description ? `
                    <p class="event-card__description">${this.escapeHtml(event.description)}</p>
                ` : ''}
            </div>
        </${tag}>
    `;
    }

    customizeArrows(selectedDates, dateStr, instance) {
        const thickChevron = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#58c8ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="16 20 8 12 16 4"></polyline>
        </svg>
    `;

        const prevArrow = instance.prevMonthNav;
        const nextArrow = instance.nextMonthNav;

        if (prevArrow) {
            prevArrow.innerHTML = thickChevron;
        }

        if (nextArrow) {
            nextArrow.innerHTML = thickChevron.replace('16 20 8 12 16 4', '8 20 16 12 8 4');
        }
    }

    escapeHtml(text) {
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;'
        };
        return String(text).replace(/[&<>"']/g, m => map[m]);
    }
}
